"use client";

import React from "react";

import Image from "next/image";
import Link from "next/link";
import ScrollingBanner from "@/components/global/ui/ScrollingBanner";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import Chip from "@/components/global/ui/Chip";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";

import {PageCommunitySchema} from "@/sanity/pages/PageCommunityTemplate";

import trademaniaIcon from "@/public/icons/trademania-dark-green.svg";
import discordIcon from "@/public/icons/Discord-Logo-Blue.svg";
import youTubeIcon from "@/public/icons/YouTube.svg";

export default function CommunitySection({
  data,
  className
}: {
  data: PageCommunitySchema;
  className?: string;
}) {

  return (
    <section className={cn(
      "relative overflow-clip",
      className
    )}>
      {data?.communityImage && (
        <Image
          className="!w-full !h-[69rem] lg:!h-auto object-cover object-[65%_50%] lg:object-center"
          src={data.communityImage}
          quality={100}
          alt="Community image"
        />
      )}

      {data?.communityScroll &&
        <ScrollingBanner
          text={data.communityScroll}
        />
      }

      <div className="relative overflow-clip">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
        />
        <DecorationLine
          className="top-0"
          orientation="horizontal"
        />

        <DecorationSquares
          className="top-[2px]"
        />

        <div className="container relative py-[13rem] lg:py-[20rem] flex flex-col items-center">
          <Chip
            image={data?.communityChip?.icon}
            text={data?.communityChip?.text}
          />

          {data?.communityText && (
            <div className="richtext prose-p:w-[min(92.5rem,100%)] prose-headings:w-[min(120rem,100%)] mb-[4rem]">
              <PortableText
                value={data.communityText}
                components={richTextComponent}
              />
            </div>
          )}

          <div className="w-[min(49rem,100%)] lg:w-[min(149.25rem,100%)] flex flex-col lg:flex-row gap-[1rem]">
            <Link
              href="#LiveEvents"
              className="grow relative transition-all group bg-[#1F382F]/[.05] hover:bg-[#1F382F]/[.06] [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)] h-[12rem] lg:h-[14rem] flex items-center justify-center gap-[1.5rem]"
            >
              <Image
                className="!w-[5.75rem]"
                src={trademaniaIcon}
                alt="Trademania icon"
              />
              <p className="mb-0 text-[2.5rem] font-medium text-black/70 uppercase leading-none">Live Events</p>
              <div className="absolute bg-[#1F382F]/10 left-0 bottom-0 w-full h-[4px]  group-hover:h-[8px] transition-[height]" />
            </Link>

            <Link
              href="#Discord"
              className="grow relative transition-all group bg-[#4D5CEF]/[.05] hover:bg-[#4D5CEF]/[.06] [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)] h-[12rem] lg:h-[14rem] flex items-center justify-center gap-[1.5rem]"
            >
              <Image
                className="!w-[6.5rem]"
                src={discordIcon}
                alt="Trademania icon"
              />
              <p className="mb-0 text-[2.5rem] font-medium text-black/70 uppercase leading-none">Discord</p>
              <div className="absolute bg-[#4D5CEF]/10 left-0 bottom-0 w-full h-[4px]  group-hover:h-[8px] transition-[height]" />
            </Link>

            <Link
              href="#YouTube"
              className="grow relative transition-all group bg-[#FF0000]/[.05] hover:bg-[#FF0000]/[.06] [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)] h-[12rem] lg:h-[14rem] flex items-center justify-center gap-[1.5rem]"
            >
              <Image
                className="!w-[6.75rem]"
                src={youTubeIcon}
                alt="Trademania icon"
              />
              <p className="mb-0 text-[2.5rem] font-medium text-black/70 uppercase leading-none">YouTube</p>
              <div className="absolute bg-[#FF0000]/10 left-0 bottom-0 w-full h-[4px]  group-hover:h-[8px] transition-[height]" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
